import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function ComboboxDemo({
  items,
  onSelect,
  searchPlaceholder,
  emptyPlaceholder,
  selectPlaceholder,
  onChange,
  shouldFilter = true,
  name,
  defaultValue,
  disabled,
  customIcon,
  className,
  minSearchLength,
  minLengthMessage,
  widthContent,
}: {
  items: {
    label: string;
    value: string;
  }[];
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  searchPlaceholder: string;
  emptyPlaceholder: string;
  selectPlaceholder: string;
  shouldFilter?: boolean; //shouldFilter is a flag that command uses to search internally, to filter using an API request this should be false
  name?: string;
  defaultValue?: string;
  disabled?: boolean;
  customIcon?: React.ReactNode;
  className?: string;
  minSearchLength?: number;
  minLengthMessage?: string;
  widthContent?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(defaultValue ?? "");
  const [searchValue, setsearchValue] = React.useState("");

  // Sync internal value with defaultValue when it changes
  React.useEffect(() => {
    setValue(defaultValue ?? "");
  }, [defaultValue]);

  return (
    <div className="w-full">
      <input type="hidden" name={name} value={value} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={disabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between truncate", className)}
          >
            {value
              ? items.find((item) => item.value === value)?.label ||
                selectPlaceholder
              : selectPlaceholder}
            {customIcon ? (
              customIcon
            ) : (
              <ChevronsUpDown className="opacity-50 size-4" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn("w-[300px] p-0", widthContent)}
        >
          <Command shouldFilter={shouldFilter}>
            <CommandInput
              placeholder={searchPlaceholder}
              onChangeCapture={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                setsearchValue(newValue);
                onChange?.(newValue);
              }}
            />
            <CommandList>
              {minSearchLength && searchValue.length < minSearchLength ? (
                <CommandEmpty>
                  {minLengthMessage ||
                    `Digite pelo menos ${minSearchLength} caracteres para pesquisar`}
                </CommandEmpty>
              ) : (
                <CommandEmpty>{emptyPlaceholder}</CommandEmpty>
              )}
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.value}
                    //ensure value is a string or current value from onSelect will be the label
                    value={item.value.toString()}
                    onSelect={(currentValue) => {
                      onSelect?.(currentValue);
                      setValue(currentValue);
                      setOpen(false);
                    }}
                  >
                    {item.label}
                    <Check
                      className={cn(
                        "ml-auto",
                        value === item.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
